import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { 
  Briefcase, 
  Target, 
  Clock, 
  AlertTriangle,
  Users,
  TrendingUp,
  Shield,
  Zap
} from "lucide-react";

const colorClasses = {
  blue: { bg: "bg-blue-50", border: "border-blue-200", icon: "bg-blue-100 text-blue-600", badge: "bg-blue-100 text-blue-700" },
  purple: { bg: "bg-purple-50", border: "border-purple-200", icon: "bg-purple-100 text-purple-600", badge: "bg-purple-100 text-purple-700" },
  pink: { bg: "bg-pink-50", border: "border-pink-200", icon: "bg-pink-100 text-pink-600", badge: "bg-pink-100 text-pink-700" },
  cyan: { bg: "bg-cyan-50", border: "border-cyan-200", icon: "bg-cyan-100 text-cyan-600", badge: "bg-cyan-100 text-cyan-700" },
  amber: { bg: "bg-amber-50", border: "border-amber-200", icon: "bg-amber-100 text-amber-600", badge: "bg-amber-100 text-amber-700" },
};

export default function PainPoints() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="para-quien"
      className="py-20 bg-white"
      data-testid="pain-points-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block text-xs uppercase tracking-[0.2em] text-galvani-purple font-body font-semibold mb-4">
            ¿Te Identificas?
          </span>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-galvani-text mb-4">
            Creado para quienes buscan
            <span className="gradient-text"> resultados reales</span>
          </h2>
          <p className="font-body text-galvani-textLight max-w-2xl mx-auto">
            Nuestra guía está diseñada para resolver los problemas específicos de dos perfiles clave.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Card 1: Agencias */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="group"
            data-testid="agency-card"
          >
            <div className="h-full p-8 lg:p-10 rounded-2xl bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
                  <Briefcase className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-xl text-galvani-text">
                    Para Agencias de Marketing
                  </h3>
                  <p className="text-sm text-galvani-textLight">PM · Account · Estratega</p>
                </div>
              </div>

              <p className="font-body text-galvani-textLight mb-6 leading-relaxed">
                Si gestionas cuentas de clientes y necesitas proveedores en los que puedas confiar sin estar encima constantemente.
              </p>

              <div className="space-y-4 mb-8">
                <h4 className="text-xs uppercase tracking-[0.15em] text-galvani-textLight font-semibold">
                  Problemas que resolvemos:
                </h4>
                <div className="grid gap-3">
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-white/60">
                    <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span className="text-galvani-text text-sm">Proveedores que incumplen plazos</span>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-white/60">
                    <Clock className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                    <span className="text-galvani-text text-sm">Revisiones infinitas que consumen tu tiempo</span>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-white/60">
                    <Shield className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span className="text-galvani-text text-sm">Estrés por quedar mal con tu cliente</span>
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t border-blue-200">
                <h4 className="text-xs uppercase tracking-[0.15em] text-galvani-textLight font-semibold mb-4">
                  Lo que buscas:
                </h4>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                    Control del proceso
                  </span>
                  <span className="px-3 py-1.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium">
                    Entregas predecibles
                  </span>
                  <span className="px-3 py-1.5 rounded-full bg-pink-100 text-pink-700 text-xs font-medium">
                    Cero sorpresas
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Card 2: CEOs Pymes */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="group"
            data-testid="ceo-card"
          >
            <div className="h-full p-8 lg:p-10 rounded-2xl bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                  <Target className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-xl text-galvani-text">
                    Para CEOs de Pymes
                  </h3>
                  <p className="text-sm text-galvani-textLight">Fundador · Director · Decisor</p>
                </div>
              </div>

              <p className="font-body text-galvani-textLight mb-6 leading-relaxed">
                Si quieres posicionar tu marca como referente del sector pero no tienes tiempo ni sabes por dónde empezar.
              </p>

              <div className="space-y-4 mb-8">
                <h4 className="text-xs uppercase tracking-[0.15em] text-galvani-textLight font-semibold">
                  Problemas que resolvemos:
                </h4>
                <div className="grid gap-3">
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-white/60">
                    <Users className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                    <span className="text-galvani-text text-sm">Tu competencia parece más grande</span>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-white/60">
                    <Zap className="w-5 h-5 text-pink-500 flex-shrink-0 mt-0.5" />
                    <span className="text-galvani-text text-sm">No sabes qué contenido crear</span>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-white/60">
                    <TrendingUp className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span className="text-galvani-text text-sm">Inviertes en marketing sin ver ROI</span>
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t border-purple-200">
                <h4 className="text-xs uppercase tracking-[0.15em] text-galvani-textLight font-semibold mb-4">
                  Lo que buscas:
                </h4>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium">
                    Autoridad en tu sector
                  </span>
                  <span className="px-3 py-1.5 rounded-full bg-pink-100 text-pink-700 text-xs font-medium">
                    Sistema automatizado
                  </span>
                  <span className="px-3 py-1.5 rounded-full bg-cyan-100 text-cyan-700 text-xs font-medium">
                    Generar leads
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
